import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { toast } from 'react-toastify';
import { useQueryClient } from 'shared-ui';

import { useMutation } from 'hooks/useMutation/useMutation';
import { useLocale } from 'hooks/useLocale/useLocale';
import { AppMessages } from 'i18n/messages';
import { useQuery } from 'hooks/useQuery/useQuery';
import { QueryKeyEnum } from 'core/global.enum';
import { fetchPasswordPolicy, updatePasswordPolicy } from 'api/auth/auth';
import type { PasswordPolicy as PasswordPolicyResponse } from 'api/auth/auth.types';

import { PasswordPolicy } from './PasswordPolicy';
import { UpdatePasswordsPolicyFormBody } from './PasswordPolicy.types';
import { usePasswordsPolicyFormValidation } from './PasswordPolicy.validation';

export const PasswordPolicyContainer = () => {
  const { formatMessage } = useLocale();
  const { schema } = usePasswordsPolicyFormValidation();
  const queryClient = useQueryClient();
  const passwordPolicy = queryClient.getQueryData<PasswordPolicyResponse>([QueryKeyEnum.PASSWORD_POLICY]);

  const formMethods = useForm<UpdatePasswordsPolicyFormBody>({
    mode: 'onChange',
    resolver: yupResolver(schema),
    defaultValues: passwordPolicy,
  });

  const { isLoading: isLoadingPasswordPolicy } = useQuery([QueryKeyEnum.PASSWORD_POLICY], fetchPasswordPolicy, {
    onSuccess: (data) => {
      formMethods.reset(data);
    },
  });

  const { mutate: onUpdatePasswordPolicy, isLoading } = useMutation(updatePasswordPolicy, {
    onSuccess: () => {
      toast.dark(formatMessage({ id: AppMessages['definitions.passwordPolicy.success'] }));
    },
  });

  const onSubmit = (body: UpdatePasswordsPolicyFormBody) => {
    onUpdatePasswordPolicy({ body: { ...body, passwordRenewalDays: body.passwordRenewalDays ?? 0 } });
  };

  return (
    <FormProvider {...formMethods}>
      <PasswordPolicy onSubmit={onSubmit} isLoading={isLoading || isLoadingPasswordPolicy} />
    </FormProvider>
  );
};
